import axios, { AxiosRequestConfig, Canceler } from "axios";
import { getToken } from "utils/localStorage";

const stagingFront = "v2.staging.ccmphp.com";
const demoFront = "demo.ccmphp.com";

// 特に時間がかかると思われる API については個別にタイムアウトを設定すること
const timeout = 180 * 1000;

const getBaseURL = () => {
  if (window.location.hostname === demoFront) {
    return "https://demo.api.ravenhp.com";
  } else if (window.location.hostname === stagingFront) {
    return "https://staging.api.ravenhp.com";
  } else if (
    window.location.hostname === "valuehp.info" ||
    window.location.hostname === "staging.valuehp.info"
  ) {
    return "https://staging.api.ravenhp.com";
  } else {
    switch (process.env.NODE_ENV) {
      case "development":
        // return "http://localhost:3000";

        return (
          process.env.REACT_APP_API_BASE_URL ||
          "https://staging.api.ravenhp.com"
        );
      case "production":
        return "https://api.ravenhp.com";
      default:
        return "https://staging.api.ravenhp.com";
    }
  }
};

const headers = {
  "Content-Type": "application/json",
  "X-Requested-With": "XMLHttpRequest",
  Authorization: "",
};

const token = getToken("agency") || getToken("store") || null;

if (token) {
  headers.Authorization = `Bearer ${token}`;
}

const api = axios.create({
  baseURL: getBaseURL(),
  headers,
  responseType: "json",
  timeout: timeout,
});

const changeToken = (newToken: string) => {
  api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
};

// 同じリクエストが重複して行われないようにする
let pending: Record<string, null> = {};
const { CancelToken } = axios;

const removePending = (
  request: AxiosRequestConfig,
  cancel: Canceler | null
) => {
  const url = request.url!.replace(request.baseURL!, "/");

  // 重複チェックに使うflagUrlを生成 (url, method, params, data)
  // paramsとdataはmethodによってはundefinedとなる

  // request.data がオブジェクトのときと JSON 文字列のときがあるので、
  // 二重エスケープしないようにしておく
  const requestData =
    typeof request.data === "string"
      ? request.data
      : JSON.stringify(request.data);

  const flagUrl = `${url}&${request.method}&${JSON.stringify(
    request.params
  )}&${requestData}`;

  // pending中のリクエストと全く同じリクエストは、キャンセルする
  if (flagUrl in pending) {
    if (cancel) {
      // リクエスト時の処理
      // リクエストをキャンセル
      cancel(JSON.stringify(request));
    } else {
      // レスポンス時の処理
      // レスポンスが返ってきたので、pendingから消す
      delete pending[flagUrl];
    }
  } else {
    // pending中のリクエストに同じリクエストが存在しないので、リクエストを行う
    // レスポンスが返ってくるまでは、pendingにflagUrlを入れておく
    if (cancel) {
      // log
      if (process.env.NODE_ENV === "development") {
        console.info("Starting Request: ", request);
      }
      pending[flagUrl] = null;
    }
  }
};

// リクエスト用
api.interceptors.request.use(
  (request) => {
    // 重複リクエストチェック
    request.cancelToken = new CancelToken((cancel) =>
      removePending(request, cancel)
    );
    return request;
  },
  (error) => {
    Promise.reject(error);
  }
);

// レスポンス用
api.interceptors.response.use(
  (response) => {
    // 開発環境ではリクエストログを出力する
    if (process.env.NODE_ENV === "development") {
      console.info("Response: ", response);
    }

    // pendingから該当のrequestを消す
    removePending(response.config, null);

    return response;
  },
  (error) => {
    if (axios.isCancel(error)) {
      const canceledRequest = JSON.parse(error.message);
      return Promise.reject(
        `Canceled - ${canceledRequest.method.toUpperCase()}: ${
          canceledRequest.url
        } (params: ${JSON.stringify(canceledRequest.params)})`
      );
    } else {
      return Promise.reject(error);
    }
  }
);

export const resetPending = () => {
  pending = {};
};

export { changeToken };
export default api;
